import { useEffect, useMemo, useState } from 'react';
import type { Episode, VideoItem } from '@/types/video';

interface PlayerDemoProps {
  video: VideoItem;
  episode: Episode | null;
  playerKey: string;
}

function parseMinutes(duration: string): number {
  const matched = duration.match(/\d+/);
  return matched ? Number(matched[0]) : 45;
}

export default function PlayerDemo({ video, episode, playerKey }: PlayerDemoProps) {
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setStarted(false);
    setPlaying(false);
    setProgress(0);
  }, [playerKey]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return Math.min(100, prev + 1);
      });
    }, 700);
    return () => window.clearInterval(timer);
  }, [playing]);

  useEffect(() => {
    if (progress >= 100) setPlaying(false);
  }, [progress]);

  const totalMinutes = useMemo(
    () => parseMinutes(episode ? episode.duration : video.duration),
    [episode, video.duration],
  );
  const elapsedMinutes = Math.floor((totalMinutes * progress) / 100);
  const ended = progress >= 100;

  const startPlayback = () => {
    if (ended) setProgress(0);
    setStarted(true);
    setPlaying(true);
  };

  const togglePlay = () => {
    if (ended) {
      setProgress(0);
      setPlaying(true);
      return;
    }
    setStarted(true);
    setPlaying((prev) => !prev);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-primary-950">
      <div className="relative aspect-video w-full overflow-hidden bg-primary-950">
        <img
          src={video.backdrop ?? video.cover}
          alt={`${video.title}播放界面`}
          title={`${video.title} - 在线播放界面演示`}
          className={`absolute inset-0 h-full w-full object-cover object-top transition-all duration-500 ${
            started ? 'scale-105 opacity-40' : 'opacity-80'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/30" />

        <div className="absolute inset-x-3 top-3 flex items-center gap-2 md:left-4 md:right-auto md:top-4">
          <span className="shrink-0 rounded bg-accent-500 px-2 py-0.5 font-label text-[10px] font-bold tracking-wide text-primary-950">
            DEMO
          </span>
          <span className="truncate rounded bg-primary-950/60 px-2 py-0.5 text-[11px] text-background-100">
            {video.title}
            {episode ? ` · ${episode.title}` : ''}
          </span>
        </div>

        {!started && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <button
              type="button"
              onClick={startPlayback}
              aria-label="开始播放演示"
              className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform duration-200 hover:scale-105 md:h-20 md:w-20"
            >
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </button>
            <p className="text-xs text-background-100 md:text-sm">点击播放 · 界面演示，无真实片源</p>
            <p className="text-[11px] text-background-300">当前集数：{episode ? episode.title : '正片'}</p>
          </div>
        )}

        {started && playing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="animate-soft-pulse flex h-14 w-14 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 md:h-16 md:w-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-accent-400" />
            </span>
            <p className="text-xs text-background-100 md:text-sm">正在播放演示 · 缓冲模拟中</p>
            <div className="loading-bar relative h-1 w-40 overflow-hidden rounded-full bg-background-50/20" />
          </div>
        )}

        {started && !playing && !ended && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              type="button"
              onClick={togglePlay}
              aria-label="继续播放"
              className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-background-50/95 text-primary-950 transition-transform duration-200 hover:scale-105"
            >
              <i className="ri-play-fill text-3xl" />
            </button>
          </div>
        )}

        {ended && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-900/80 text-accent-400">
              <i className="ri-restart-line text-2xl" />
            </span>
            <p className="text-sm font-medium text-background-50">演示播放已结束</p>
            <p className="max-w-sm text-[11px] leading-relaxed text-background-300">
              本站播放器仅展示播放状态与进度效果，无法加载真实视频资源。你可以切换其他剧集继续体验界面。
            </p>
            <button
              type="button"
              onClick={startPlayback}
              className="mt-1 cursor-pointer whitespace-nowrap rounded-md border border-background-50/40 px-4 py-1.5 text-xs text-background-50 transition-colors hover:bg-background-50/10"
            >
              重新播放
            </button>
          </div>
        )}

        {started && (
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-primary-950 via-primary-950/70 to-transparent px-3 pb-3 pt-8 md:px-4">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? '暂停' : '播放'}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-primary-950"
            >
              <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-base`} />
            </button>
            <span className="shrink-0 font-label text-[11px] text-background-100">
              {String(elapsedMinutes).padStart(2, '0')}:00 / {totalMinutes}:00
            </span>
            <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-background-50/25">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-accent-500 transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="hidden shrink-0 items-center gap-2 text-background-200 sm:flex">
              <i className="ri-volume-up-line text-sm" />
              <i className="ri-hd-line text-sm" />
              <i className="ri-fullscreen-line text-sm" />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-primary-900 bg-primary-950 px-4 py-2.5">
        <p className="text-[11px] text-primary-300">
          <i className="ri-information-line mr-1" />
          播放器为界面演示，不接入流媒体服务
        </p>
        <p className="text-[11px] text-primary-300">进度 {Math.round(progress)}%</p>
      </div>
    </div>
  );
}